"use client"
import {
  Avatar,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
  Navbar as HuiNavbar,
  NavbarBrand,
  NavbarContent,
  semanticColors
} from "@heroui/react"
import React from "react"
import Link from "next/link"
import { faArrowRight, faBars, faShield, faUser } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useSession } from "@/modules/auth/api"
import { useTheme } from "next-themes"
import { useTranslations } from "next-intl"
import { authClient } from "@/lib/auth-client"
import ThemeSelect from "@/components/shared/ThemeSelect"
import { getUserDisplayName } from "@/server/lib/user"

export const Navbar = ({ onOpen, className, isPublic }: { onOpen?: () => void, className?: string, isPublic: boolean }) => {
  const t = useTranslations("Navbar")
  const { user } = useSession()
  const displayName = user ? getUserDisplayName(user) : ""
  const { theme } = useTheme()
  const primaryColor = theme === 'dark' ?
    semanticColors.dark.primary as { DEFAULT: string, foreground: string } :
    semanticColors.light.primary as { DEFAULT: string, foreground: string }

  async function handleLogout() {
    await authClient.signOut()
  }

  return (
    <HuiNavbar
      maxWidth={"full"}
      height={"4rem"}
      isBordered={false}
      isBlurred={true}
      className={`bg-content1/80 backdrop-blur-md shadow-sm ${className}`}
    >
      <div className={"flex gap-4 items-center"}>
        {!isPublic && user && (
          <button
            type="button"
            className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-default-500 hover:bg-default-100 focus:outline-none focus:ring-2 focus:ring-primary/20 md:hidden transition-colors"
            onClick={() => onOpen?.()}
          >
            <span className="sr-only">{t('openSidebar')}</span>
            <FontAwesomeIcon icon={faBars} className="w-5 h-5" />
          </button>
        )}
        <NavbarBrand>
          <Link href="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <h1 className="font-bold text-lg min-[400px]:text-xl text-default-900">Nexum Space VIP</h1>
          </Link>
        </NavbarBrand>
      </div>

      <NavbarContent as="div" justify="end" className={"flex-shrink-0 !basis-auto gap-4"}>
        <div className="hidden md:block">
          <ThemeSelect />
        </div>
        {!isPublic && user && (
          <Dropdown placement="bottom-end">
            <DropdownTrigger>
              <div className={"flex gap-3 items-center cursor-pointer hover:opacity-80 transition-opacity"}>
                <div className={"hidden lg:block text-right"}>
                  <div className={"font-semibold text-sm text-default-900"}>
                    {displayName}
                  </div>
                  <div className={"text-xs text-default-500"}>
                    {user.email}
                  </div>
                </div>
                <Avatar
                  isBordered
                  as="button"
                  color="primary"
                  size="sm"
                  className="ring-2 ring-primary/20"
                  src={`https://ui-avatars.com/api/?name=${displayName}&background=${primaryColor.DEFAULT.slice(
                    1)}&color=${primaryColor.foreground.slice(1)}&size=100&format=png`}
                />
              </div>
            </DropdownTrigger>
            <DropdownMenu
              aria-label="Profile Actions"
              variant="flat"
              className="min-w-[200px]"
            >
              <DropdownItem key="user" className="h-12 gap-2 opacity-100">
                <div>
                  <p className="font-semibold text-sm">{t('signedInAs')}</p>
                  <p className="text-xs text-default-500">{user.email}</p>
                </div>
              </DropdownItem>
              <DropdownItem
                key="profile"
                href="/profile"
                className="gap-3"
                startContent={<FontAwesomeIcon icon={faUser} className="w-4 h-4" fixedWidth />}
              >
                {t('profile')}
              </DropdownItem>
              <DropdownItem
                key="security-settings"
                href="/security-settings"
                className="gap-3"
                startContent={<FontAwesomeIcon icon={faShield} className="w-4 h-4" fixedWidth />}
              >
                {t('securitySettings')}
              </DropdownItem>
              <DropdownItem
                key="logout"
                color="danger"
                className="gap-3"
                onPress={handleLogout}
                startContent={<FontAwesomeIcon icon={faArrowRight} className="w-4 h-4" fixedWidth />}
              >
                {t('signOut')}
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>
        )}
      </NavbarContent>
    </HuiNavbar>
  )
}
